import React, { useEffect } from 'react'
import { Form } from 'antd';
import { useFormik } from 'formik';
import { useMutation } from 'react-query';
import { api } from '../../../../../api/api';
import { QAndAObj, ObjDB } from '../../../../../types/apiTypes';
import FormStatus from '../../../../../components/form/FormStatus/FormStatus';
import { useFormStatus } from '../../../../../utils/hooks/useFormStatus';
import EditPlaceholder from '../../../../../components/common/EditPlaceholder/EditPlaceholder';
import { getAntField } from '../../../../../utils/form';
import { QAndAFormFields } from '../../../../../types/formFieldTypes';
import { errorNotification, invalidateQueries } from '../../../../../utils/rest';
import { SwitchQAndAItems } from '../../../../../types/propsTypes';
import { clearQAndAErrors, initialQAndAFields, qAndAValidate } from '../../formData/QAndAFormData';
import QAndAFormInputs from '../QAndAFormInputs/QAndAFormInputs';
import { queryKeysQAndA } from '../../../../../static/data/queryKeys';

type QAndAEditFormProps = {
    editItem: ObjDB<QAndAObj> | null
    switchData: SwitchQAndAItems
    setOpenedForm: React.Dispatch<React.SetStateAction<boolean>>
    setEditItem: React.Dispatch<React.SetStateAction<ObjDB<QAndAObj> | null>>
}

const QAndAEditForm: React.FC<React.PropsWithChildren<QAndAEditFormProps>> = React.memo((props) => {
    const {
        editItem, setOpenedForm, setEditItem,
        switchData,
    } = props

    const formStatus = useFormStatus()

    const {
        errors, values, touched,
        setFieldValue, handleChange,
        submitForm, setValues, setErrors,
    } = useFormik<QAndAFormFields>({
        initialValues: initialQAndAFields,
        onSubmit: (values) => {
            mutate(values)
        },
        validateOnChange: false,
        validate: qAndAValidate
    })

    const antField = getAntField<QAndAFormFields>(values, errors, touched)

    const {
        mutate
    } = useMutation(
        async (values: QAndAFormFields) => {
            if (editItem) return await api.qAndA.edit(values, editItem)
        },
        {
            mutationKey: 'qAndA-edit',
            onSuccess() {
                invalidateQueries(queryKeysQAndA[1])
                formStatus.setMessage('Данные успешно изменены')
                formStatus.setStatus('success')
                setTimeout(clearEditItem, 1000)
            },
            onError: (err: Error) => errorNotification(err, 'Ошибка редактирования', formStatus),
            onMutate() {
                formStatus.setMessage('Отправка данных')
                formStatus.setStatus('fetching')
            }
        }
    )

    const clearForm = () => {
        setValues(initialQAndAFields)
        setErrors(clearQAndAErrors)
    }

    const clearEditItem = () => {
        clearForm()
        setOpenedForm(false)
        setTimeout(() => {
            setEditItem(null)
            formStatus.reset()
        }, 300)
    }

    const resetChanges = () => {
        if (editItem) {
            const { id, ...values } = editItem
            setValues(values)
            setErrors(clearQAndAErrors)
        }
    }

    useEffect(() => {
        if (editItem) return resetChanges()
        clearForm()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editItem])

    if (!editItem) return <EditPlaceholder />

    return (
        <Form
            fields={[
                antField('course_id', {withFalsy: true}),
                antField('question'),
                antField('answer'),
            ]}
        >
            {
                editItem &&
                <p className="uif-ant-form-title">
                    ID редактируемого объекта: <span>{editItem.id}</span>
                </p>
            }

            <QAndAFormInputs
                switchData={switchData}
                values={values}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                resetChanges={resetChanges}
                submitForm={submitForm}
            />

            <FormStatus formStatus={formStatus} />
        </Form>
    )
})

export default QAndAEditForm